import React from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function MedalliaComparison() {
  const consultationLink = "https://kronologic.me/1-click/zWxqVT2ASOytpMCtSReRDWxbBRVHiUbHjzqAm5x3Arg";

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex justify-between items-center py-8">
            <a href="https://icpedge.com" className="w-48">
              <img 
                src="/logo.png" 
                alt="ICP EDGE Logo" 
                className="w-full h-auto brightness-0 invert"
              />
            </a>
            <div className="flex items-center gap-6">
              <Link 
                to="/tools/decision-tree" 
                className="text-white hover:text-blue-100 transition-colors font-semibold"
              >
                Find Your Edge
              </Link>
              <Link 
                to="/tools/conversion-unlocker" 
                className="text-white hover:text-blue-100 transition-colors font-semibold"
              >
                Fix Conversion Blockers
              </Link>
              <a 
                href={consultationLink}
                className="px-6 py-3 rounded-lg font-semibold bg-white text-blue-600 hover:bg-blue-50 transition-colors"
              >
                Partner With Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 max-w-6xl py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="prose prose-lg max-w-none">
            <h1 className="text-4xl font-bold mb-8">
              Elevating Your CX Practice:<br />
              Where Medallia Data Meets Customer Psychology
            </h1>

            <div className="bg-blue-50 p-8 rounded-xl mb-12">
              <h2 className="text-2xl font-bold mb-4">The Data-Insight Gap Every CX Leader Faces</h2>
              <p className="mb-6">
                As CX professionals, we're swimming in customer data. Platforms like Medallia excel at what they do: 
                collecting comprehensive feedback across touchpoints, unifying fragmented data, and visualizing customer journeys.
              </p>
              <p>
                When your clients invest in Medallia, they gain powerful capabilities to capture the what of customer 
                behavior—but often still struggle with the why behind those actions.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-8 rounded-xl border-2 border-gray-200">
                <h3 className="text-xl font-bold mb-6">Medallia Delivers</h3>
                <ul className="space-y-4">
                  {[
                    'Unified customer feedback from all channels',
                    'Operational metrics and journey visualization',
                    'Pattern identification across touchpoints',
                    'Tactical action recommendations'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white p-8 rounded-xl border-2 border-blue-200">
                <h3 className="text-xl font-bold mb-6">ConsumerDNA™ Reveals</h3>
                <ul className="space-y-4">
                  {[
                    'Psychological drivers behind customer decisions',
                    'Emotional barriers preventing conversion',
                    'Hidden motivations analytics alone cannot capture',
                    'Strategic insights that transform journey maps from observation to explanation'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6">The Competitive Advantage of Complementary Capabilities</h2>
              <p className="mb-6">
                Your consultancy likely already works with clients using Medallia. While they value the platform's 
                robust data collection, they're increasingly asking questions Medallia alone can't answer:
              </p>
              <blockquote className="border-l-4 border-blue-500 pl-6 my-8 italic">
                "We can see where customers abandon the journey, but why do they lose confidence at this particular stage?"
              </blockquote>
              <blockquote className="border-l-4 border-blue-500 pl-6 my-8 italic">
                "Our NPS improved after implementing changes, but what specific psychological need did we accidentally satisfy?"
              </blockquote>
              <p>
                By integrating ConsumerDNA™ insights with your clients' Medallia data, your practice gains the ability 
                to transform their understanding from "here's what's happening" to "here's why it's happening—and what 
                to do about it."
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6">How Leading Consultancies Leverage Both</h2>
              <p className="mb-6">
                The most sophisticated CX practices are creating premium service offerings that combine:
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">1</div>
                  <div>
                    <p>Medallia's comprehensive data collection and journey visualization</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">2</div>
                  <div>
                    <p>ConsumerDNA™'s psychological interpretation and motivation analysis</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">3</div>
                  <div>
                    <p>Your consultancy's unique strategic methodology and implementation expertise</p>
                  </div>
                </div>
              </div>
              <p className="mt-6">
                The result? Deliverables that justify higher rates, expanded project scopes, and strategic engagements 
                that competitors simply cannot match.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-xl mb-12">
              <h2 className="text-2xl font-bold mb-4">
                Your Clients Already Have the Data—Help Them Understand What It Really Means
              </h2>
              <p className="text-lg">
                Your clients have invested significantly in platforms like Medallia. ConsumerDNA™ helps your consultancy 
                extract maximum value from that investment by revealing the critical psychological context behind the numbers.
              </p>
            </div>

            <div className="text-center">
              <p className="text-lg mb-8">
                Ready to elevate your practice with insights your competitors can't deliver? Let's explore how 
                ConsumerDNA™ can enhance your unique methodology while preserving your client relationships.
              </p>
              <a 
                href={consultationLink}
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Partner With Us
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}